import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Play, Target, Clock, Trophy, Star, Zap, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';
import Footer from '../components/Footer';

const StepCard = ({ number, title, description, icon: Icon }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5, delay: number * 0.1 }}
    className="flex gap-4 items-start"
  >
    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
      <span className="text-xl font-bold text-primary">{number}</span>
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-heading font-bold">{title}</h3>
      </div>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const HowToPlayPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="text-center mb-8">
            <Play className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gradient-primary">
              How to Play
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Master JailBirds in minutes with this complete guide
            </p>
          </div>

          {/* Quick Start */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-2">
              <Zap className="w-6 h-6 text-primary" />
              Quick Start Guide
            </h2>
            <div className="space-y-6">
              <StepCard
                number={1}
                title="Create Your Nickname"
                description="Before your first game, you'll be asked to create a unique nickname. This is how you'll appear on the leaderboard. Choose something memorable!"
                icon={Star}
              />
              <StepCard
                number={2}
                title="View the Mugshot"
                description="Each round shows you a real mugshot from Mecklenburg County arrest records. Take a moment to observe the image before making your guess."
                icon={Target}
              />
              <StepCard
                number={3}
                title="Choose the Charge"
                description="Four possible criminal charges will be displayed. Select the one you think matches the person in the mugshot. Trust your instincts!"
                icon={CheckCircle}
              />
              <StepCard
                number={4}
                title="See the Result"
                description="After each answer, you'll see if you were correct and the actual charge. Learn from each round to improve your guessing skills!"
                icon={Trophy}
              />
            </div>
          </div>

          {/* Game Structure */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Game Structure</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Target className="w-5 h-5 text-primary" />
                  Regular Questions (1-11)
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                    <span>11 mugshot questions per game</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                    <span>No time limit - take your time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                    <span>100 points for each correct answer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                    <span>Four answer choices per question</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  JailBird Bonus (Question 12)
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                    <span>Special bonus round with timer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                    <span>Faster answers = higher multiplier</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                    <span>Can significantly boost your final score</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-yellow-500 mt-1 flex-shrink-0" />
                    <span>Answer within 5 seconds for max bonus</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Scoring System */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-primary" />
              Scoring System
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-3 px-4 font-semibold">Action</th>
                    <th className="py-3 px-4 font-semibold">Points</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="py-3 px-4">Correct answer (regular)</td>
                    <td className="py-3 px-4 text-green-500 font-semibold">+100 points</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-3 px-4">Wrong answer</td>
                    <td className="py-3 px-4 text-muted-foreground">0 points (no penalty)</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-3 px-4">Bonus round (0-5 seconds)</td>
                    <td className="py-3 px-4 text-yellow-500 font-semibold">+200-300 points</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-3 px-4">Bonus round (5-10 seconds)</td>
                    <td className="py-3 px-4 text-yellow-500 font-semibold">+100-200 points</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4">Perfect game (all correct)</td>
                    <td className="py-3 px-4 text-primary font-semibold">1,100+ points possible!</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Tips & Strategies */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Tips & Strategies</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h3 className="font-semibold text-green-500 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Do This
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Look at facial expressions and demeanor</li>
                  <li>• Consider age and appearance clues</li>
                  <li>• Trust your gut instinct</li>
                  <li>• Play regularly to learn patterns</li>
                  <li>• Stay calm during the bonus round</li>
                </ul>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h3 className="font-semibold text-red-500 mb-2 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Avoid This
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Don't overthink - first instincts are often right</li>
                  <li>• Don't assume based on stereotypes</li>
                  <li>• Don't rush regular questions unnecessarily</li>
                  <li>• Don't get discouraged by wrong answers</li>
                  <li>• Don't forget - it's just a game!</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Leaderboard Info */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-primary" />
              Climbing the Leaderboard
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Your scores are automatically saved and ranked against other players. There are four leaderboard categories:
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
                  <h4 className="font-semibold">Daily</h4>
                  <p className="text-sm">Resets at midnight UTC</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
                  <h4 className="font-semibold">Weekly</h4>
                  <p className="text-sm">Resets every Sunday</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Clock className="w-8 h-8 text-primary mx-auto mb-2" />
                  <h4 className="font-semibold">Monthly</h4>
                  <p className="text-sm">Resets on the 1st</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Trophy className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                  <h4 className="font-semibold">All-Time</h4>
                  <p className="text-sm">Permanent records</p>
                </div>
              </div>
            </div>
          </div>

          {/* Ready to Play CTA */}
          <div className="bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30 rounded-lg p-8 text-center">
            <BirdMascot size={64} className="mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Ready to Test Your Skills?</h2>
            <p className="text-muted-foreground mb-6">
              Now that you know the rules, it's time to play!
            </p>
            <Link to="/">
              <Button size="lg" className="gap-2">
                <Play className="w-5 h-5" />
                Start Playing Now
              </Button>
            </Link>
          </div>

        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default HowToPlayPage;